/**
 * Deployment site configuration.
 *
 * Reads `window.__PPOS_SITE_CONFIG__` published by /site-config.js, which is
 * loaded by index.html before the app bundle. That file is per-deployment data
 * (see ppos-pwa/deploy/tenant/), NOT part of the compiled app — so a
 * white-label server can point labels/QR codes at its own domains
 * (e.g. alshaam.my → shop.alshaam.my) without rebuilding.
 *
 * Every fallback here is deliberately conservative: the house brand
 * (PharmaPOS on ppos.my) applies only when no config is present.
 */

/**
 * Which product UI a deployment runs.
 *
 *   pharmacy — full pharmaceutical stock record (generic name, poison flags,
 *              strength, dispensing instruction, drug warning, multi-pack).
 *   retail   — simplified stall/shop UI: plain stock items with one packing.
 *
 * Decided per hostname from /site-config.js, never compiled in, so the same
 * build serves a pharmacy and a retail stall.
 */
export type SiteProfile = 'pharmacy' | 'retail';

export interface DomainSiteConfig {
  /** Storefront base URL for production hosts of this domain. */
  mallUrl: string;
  /** Storefront base URL for demo hosts of this domain (defaults to mallUrl). */
  demoMallUrl?: string;
  /** Product UI for hosts of this domain (defaults to 'pharmacy'). */
  profile?: SiteProfile;
  /**
   * Product name for hosts of this domain. Wins over the top-level `brand`,
   * so the SAME build shows "PPOS" on ppos.my and "PharmaPOS" on
   * pharmapos.com without a rebuild.
   */
  brand?: string;
  /** PWA manifest (install name) for production hosts of this domain. */
  manifestUrl?: string;
  /** PWA manifest (install name) for demo hosts of this domain. */
  demoManifestUrl?: string;
}

export interface SiteConfig {
  /** Fallback product name (white-label servers rename it). */
  brand?: string;
  /** Fallback UI profile for hosts that match no `domains` key. */
  profile?: SiteProfile;
  /** Keyed by ROOT domain; the most specific matching suffix wins. */
  domains?: Record<string, DomainSiteConfig>;
  /** Fallback for hosts that match no key. */
  default?: DomainSiteConfig;
}

declare global {
  interface Window {
    __PPOS_SITE_CONFIG__?: SiteConfig;
  }
}

const HOUSE_BRAND = 'PharmaPOS';
const HOUSE_FALLBACK: DomainSiteConfig = {
  mallUrl: 'https://mall.ppos.my',
  demoMallUrl: 'https://mall.demo.ppos.my',
};

/**
 * Built-in brand per house domain. Keeps the name correct even when the
 * deployed /site-config.js predates per-domain branding (older tenant files).
 */
const HOUSE_DOMAIN_BRANDS: Record<string, string> = {
  'ppos.my': 'PPOS',
  'pharmapos.com': 'PharmaPOS',
};

function config(): SiteConfig {
  return (typeof window !== 'undefined' && window.__PPOS_SITE_CONFIG__) || {};
}

/** Demo environments carry `demo` as one of the hostname labels. */
export function isDemoHostname(hostname: string = location.hostname): boolean {
  return hostname.split('.').includes('demo');
}

/**
 * Longest key in `keys` that `hostname` equals or is a subdomain of.
 * Matches on a dot boundary so `notppos.my` never matches `ppos.my`.
 */
function matchSuffix(hostname: string, keys: string[]): string | null {
  const sorted = [...keys].sort((a, b) => b.length - a.length);
  return sorted.find((key) => hostname === key || hostname.endsWith(`.${key}`)) ?? null;
}

/** Root-domain key from site-config that matches `hostname`, or null. */
export function matchedDomainKey(hostname: string = location.hostname): string | null {
  return matchSuffix(hostname, Object.keys(config().domains ?? {}));
}

/**
 * Product name for titles, headings and printed posters.
 *
 * Resolution order:
 *   1. `domains[<matched root domain>].brand`  — per-domain rename
 *   2. built-in house brand for a known house domain (ppos.my → PPOS)
 *   3. top-level `brand`                        — whole-deployment rename
 *   4. 'PharmaPOS'
 */
export function siteBrand(hostname: string = location.hostname): string {
  const key = matchedDomainKey(hostname);
  const domainBrand = key ? config().domains?.[key]?.brand?.trim() : undefined;
  if (domainBrand) return domainBrand;

  const houseKey = matchSuffix(hostname, Object.keys(HOUSE_DOMAIN_BRANDS));
  const houseBrand = houseKey ? HOUSE_DOMAIN_BRANDS[houseKey] : undefined;
  if (houseBrand) return houseBrand;

  const brand = config().brand?.trim();
  return brand && brand.length > 0 ? brand : HOUSE_BRAND;
}

/**
 * UI profile for the current host.
 *
 * Resolution order:
 *   1. `domains[<matched root domain>].profile` — per-domain (ppos.my → retail)
 *   2. top-level `profile`                      — whole-deployment
 *   3. 'pharmacy'
 *
 * There is deliberately NO built-in host→profile map: ppos.my and
 * pharmapos.com are served from the SAME web root, so the decision must come
 * from the deployment's site-config.js.
 */
export function siteProfile(hostname: string = location.hostname): SiteProfile {
  const key = matchedDomainKey(hostname);
  const domainProfile = key ? config().domains?.[key]?.profile : undefined;
  if (domainProfile) return domainProfile;
  return config().profile ?? 'pharmacy';
}

/** True when this deployment shows the simplified retail-stall UI. */
export function isRetailProfile(hostname: string = location.hostname): boolean {
  return siteProfile(hostname) === 'retail';
}

/**
 * PWA manifest to link for the current host, or null to keep the one baked
 * into the build (vite-plugin-pwa's /manifest.webmanifest).
 *
 * The install name on a phone comes from the manifest, not from the tab title,
 * so a branded deployment needs its own file — see public/manifest-ppos.webmanifest
 * and the `manifestUrl` / `demoManifestUrl` keys in /site-config.js.
 */
export function siteManifestUrl(hostname: string = location.hostname): string | null {
  const key = matchedDomainKey(hostname);
  const entry = key ? config().domains?.[key] : undefined;
  const demo = isDemoHostname(hostname);
  const url = (demo ? entry?.demoManifestUrl : entry?.manifestUrl)?.trim();
  return url && url.length > 0 ? url : null;
}

/**
 * Public product host shown in copy ("sign in at …", "connect your server to …").
 * Uses the matched root domain (never a www/demo subdomain), so it stays correct
 * on every deployment without hardcoding.
 */
export function posHostName(hostname: string = location.hostname): string {
  return matchedDomainKey(hostname) ?? hostname;
}

/**
 * Storefront base URL for the environment the user is currently on.
 *
 * Production host of `alshaam.my` → its configured mallUrl (`shop.alshaam.my`);
 * demo host of the same domain → its demoMallUrl. QR posters printed from a
 * demo/staging instance therefore never point at the live storefront.
 */
export function mallBaseUrl(hostname: string = location.hostname): string {
  const key = matchedDomainKey(hostname);
  const entry = (key ? config().domains?.[key] : undefined) ?? config().default ?? HOUSE_FALLBACK;
  const url = isDemoHostname(hostname) ? entry.demoMallUrl ?? entry.mallUrl : entry.mallUrl;
  return (url && url.length > 0 ? url : HOUSE_FALLBACK.mallUrl).replace(/\/+$/, '');
}
